import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Lock, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { CoRegulateMark } from '../components/Logo';
import { PrimaryButton } from '../components/brand/BrandButton';
import { BrandCard } from '../components/brand/BrandCard';
import { BrandInput } from '../components/brand/BrandInput';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [validatingToken, setValidatingToken] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [resetComplete, setResetComplete] = useState(false);
    const [error, setError] = useState('');

    // Validate token on mount
    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setTokenValid(false);
                setValidatingToken(false);
                return;
            }

            try {
                await axios.get(`${API}/api/auth/validate-reset-token?token=${token}`);
                setTokenValid(true);
            } catch (err) {
                setTokenValid(false);
            } finally {
                setValidatingToken(false);
            }
        };

        validateToken();
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        setLoading(true);

        try {
            await axios.post(`${API}/api/auth/reset-password`, {
                token,
                new_password: password
            });
            setResetComplete(true);
            toast.success('Password reset successfully!');
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
            toast.error('Failed to reset password');
        } finally {
            setLoading(false);
        }
    };

    // Loading state
    if (validatingToken) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FDF8F4] flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-[#1D6B6B] mx-auto" />
                    <BodyText className="mt-4 text-[#7D8E95]">Validating reset link...</BodyText>
                </div>
            </div>
        );
    }

    // Invalid or expired token
    if (!tokenValid && !resetComplete) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FDF8F4] flex flex-col">
                <div className="p-4">
                    <button
                        onClick={() => navigate('/auth?mode=login')}
                        className="flex items-center gap-2 text-[#1D6B6B] hover:text-[#145555] transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span className="text-sm font-medium">Back to sign in</span>
                    </button>
                </div>

                <div className="flex-1 flex items-center justify-center px-4 pb-12">
                    <div className="glass-card rounded-2xl p-5">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <XCircle className="w-8 h-8 text-red-500" />
                        </div>
                        <Heading level={2} className="mb-4">
                            Link expired or invalid
                        </Heading>
                        <BodyText className="text-[#7D8E95] mb-6">
                            This password reset link has expired or is invalid. Please request a new one.
                        </BodyText>
                        <PrimaryButton
                            onClick={() => navigate('/forgot-password')}
                            className="w-full"
                            size="lg"
                        >
                            Request new link
                        </PrimaryButton>
                    </div>
                </div>
            </div>
        );
    }

    // Success state
    if (resetComplete) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FDF8F4] flex items-center justify-center px-4">
                <div className="glass-card rounded-2xl p-5">
                    <div className="w-16 h-16 bg-[#1D6B6B]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-8 h-8 text-[#1D6B6B]" />
                    </div>
                    <Heading level={2} className="mb-4">
                        Password reset complete
                    </Heading>
                    <BodyText className="text-[#7D8E95] mb-6">
                        Your password has been successfully reset. You can now sign in with your new password.
                    </BodyText>
                    <PrimaryButton
                        onClick={() => navigate('/auth?mode=login')}
                        className="w-full"
                        size="lg"
                        data-testid="go-to-login-btn"
                    >
                        Sign in
                    </PrimaryButton>
                </div>
            </div>
        );
    }

    // Reset form
    return (
        <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FDF8F4] flex flex-col">
            <div className="p-4">
                <button
                    onClick={() => navigate('/auth?mode=login')}
                    className="flex items-center gap-2 text-[#1D6B6B] hover:text-[#145555] transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    <span className="text-sm font-medium">Back to sign in</span>
                </button>
            </div>

            <div className="flex-1 flex items-center justify-center px-4 pb-12">
                <div className="glass-card rounded-2xl p-5">
                    <div className="flex flex-col items-center mb-8">
                        <CoRegulateMark size={48} />
                        <Heading level={2} className="mt-4 text-center">
                            Create new password
                        </Heading>
                        <BodyText className="text-center mt-2 text-[#7D8E95]">
                            Choose a strong password for your account
                        </BodyText>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <BrandInput
                            id="password"
                            name="password"
                            type="password"
                            label="New password"
                            placeholder="Enter new password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={6}
                            icon={<Lock className="w-5 h-5" />}
                            data-testid="new-password-input"
                        />

                        <BrandInput
                            id="confirmPassword"
                            name="confirmPassword"
                            type="password"
                            label="Confirm password"
                            placeholder="Confirm new password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            minLength={6}
                            icon={<Lock className="w-5 h-5" />}
                            error={error}
                            data-testid="confirm-password-input"
                        />

                        <PrimaryButton
                            type="submit"
                            className="w-full mt-6"
                            size="xl"
                            disabled={loading}
                            data-testid="reset-password-btn"
                        >
                            {loading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                'Reset password'
                            )}
                        </PrimaryButton>
                    </form>

                    <SmallText className="text-center mt-6 text-[#7D8E95]">
                        Password must be at least 6 characters
                    </SmallText>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
